import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { Button, DialogActions, DialogContentText } from '@mui/material';
import {
	IProviderPayableLine,
	IProviderPayableLinePayment,
	IProviderPayableLinePaymentCreateDTO,
	ProviderPayableLinePaymentTypeEnum,
} from 'features/salesOrder/types';
import {
	createProviderPayableLinePayment,
	getProviderPayableLinePayments,
	updateProviderPayableLinePayment,
} from 'features/salesOrder/services';
import { PaymentForm } from './PaymentForm';
import { formatDateTimeToShowUser } from 'features/common/helpers';
import { isAdminOrSuperAdmin } from 'features/auth/helpers';
import { RootState } from 'store';
import { extractErrorMessage } from '../../../../../features/quotation/helpers';

const PAYMENT_TYPE_LABELS: Record<ProviderPayableLinePaymentTypeEnum, string> = {
	[ProviderPayableLinePaymentTypeEnum.CorporateCard]: 'T. Corporativa',
	[ProviderPayableLinePaymentTypeEnum.C2A]: 'C2A',
	[ProviderPayableLinePaymentTypeEnum.BankTransfer]: 'Transferencia',
	[ProviderPayableLinePaymentTypeEnum.Check]: 'Cheque',
	[ProviderPayableLinePaymentTypeEnum.Cash]: 'Efectivo',
	[ProviderPayableLinePaymentTypeEnum.Other]: 'Otro',
};

const PAYMENT_TYPE_COLORS: Record<
	ProviderPayableLinePaymentTypeEnum,
	'default' | 'primary' | 'secondary' | 'info' | 'warning' | 'success' | 'error'
> = {
	[ProviderPayableLinePaymentTypeEnum.CorporateCard]: 'secondary',
	[ProviderPayableLinePaymentTypeEnum.C2A]: 'info',
	[ProviderPayableLinePaymentTypeEnum.BankTransfer]: 'primary',
	[ProviderPayableLinePaymentTypeEnum.Check]: 'default',
	[ProviderPayableLinePaymentTypeEnum.Cash]: 'success',
	[ProviderPayableLinePaymentTypeEnum.Other]: 'default',
};

interface Props {
	open: boolean;
	line: IProviderPayableLine | null;
	onClose: () => void;
	onPaymentSaved: () => void;
}

export const PaymentsModal = ({ open, line, onClose, onPaymentSaved }: Props): JSX.Element => {
	const { auth } = useSelector((state: RootState) => state);
	const canEdit = isAdminOrSuperAdmin(auth.user);

	const [payments, setPayments] = useState<IProviderPayableLinePayment[]>([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [editingPayment, setEditingPayment] = useState<IProviderPayableLinePayment | null>(null);
	const [showForm, setShowForm] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [pendingData, setPendingData] = useState<IProviderPayableLinePaymentCreateDTO | null>(null);
	const [copiedLineId, setCopiedLineId] = useState(false);

	const handleCopyLineId = () => {
		if (!line) return;
		navigator.clipboard.writeText(line.id);
		setCopiedLineId(true);
		setTimeout(() => {
			setCopiedLineId(false);
		}, 2000);
	};

	// Importante:
	// Redondeamos siempre a 2 decimales porque JS puede dejar valores como 0.659999999
	// y eso hace que una validación de 0.66 contra el pendiente falle aunque visualmente sea correcto.
	const roundMoney = (value: number): number => Number(value.toFixed(2));

	const fetchPayments = useCallback(async () => {
		if (!line) return;
		try {
			setLoading(true);
			setError(null);
			const res = await getProviderPayableLinePayments(line.id);
			setPayments(res.data);
		} catch (error) {
			setError(extractErrorMessage(error, 'No se pudieron cargar los pagos.'));
		} finally {
			setLoading(false);
		}
	}, [line]);

	useEffect(() => {
		if (open && line) {
			fetchPayments();
			setShowForm(false);
			setEditingPayment(null);
		}
	}, [open, line, fetchPayments]);

	const handleSave = async (data: IProviderPayableLinePaymentCreateDTO) => {
		if (!line) return;

		if (editingPayment) {
			setPendingData(data);
			setConfirmOpen(true);
			return;
		}

		await doSave(data);
	};

	const doSave = async (data: IProviderPayableLinePaymentCreateDTO) => {
		if (!line) return;

		try {
			setSaving(true);
			setError(null);

			if (editingPayment) {
				await updateProviderPayableLinePayment(editingPayment.id, data);
			} else {
				await createProviderPayableLinePayment(line.id, data);
			}

			await fetchPayments();
			setShowForm(false);
			setEditingPayment(null);
			onPaymentSaved();
		} catch (error) {
			setError(extractErrorMessage(error, 'Error al guardar el pago. Intentá de nuevo.'));
		} finally {
			setSaving(false);
		}
	};

	const handleConfirmEdit = async () => {
		setConfirmOpen(false);
		if (pendingData) {
			await doSave(pendingData);
			setPendingData(null);
		}
	};

	const handleCancelConfirm = () => {
		setConfirmOpen(false);
		setPendingData(null);
	};

	const handleEdit = (payment: IProviderPayableLinePayment) => {
		setEditingPayment(payment);
		setShowForm(true);
	};

	const handleAddNew = () => {
		setEditingPayment(null);
		setShowForm(true);
	};

	const handleCancelForm = () => {
		setShowForm(false);
		setEditingPayment(null);
	};

	const totalPaid = roundMoney(payments.reduce((sum, p) => sum + p.amount, 0));
	const remaining = line ? roundMoney(line.payableAmount - totalPaid) : 0;

	// Importante:
	// Para edición, el máximo permitido es lo pendiente actual + el monto del pago que estoy editando.
	// Además lo redondeamos para evitar errores por precisión decimal.
	const maxAmountForForm = roundMoney(remaining + (editingPayment?.amount ?? 0));

	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
				<DialogTitle>
					<Box display="flex" justifyContent="space-between" alignItems="flex-start">
						<Box>
							<Typography variant="h6">Pagos al proveedor</Typography>
							{line && (
								<>
									<Typography variant="body2" color="text.secondary">
										{line.providerName}
										{line.ticket ? ` · Ticket ${line.ticket}` : ''}
										{line.passengerName ? ` · ${line.passengerName}` : ''}
									</Typography>
									<Box display="flex" alignItems="center" gap={0.5}>
										<Typography variant="body2" color="text.secondary">
											Orden de compra: {line.id}
										</Typography>
										<Tooltip title={copiedLineId ? 'Copiado satisfactoriamente' : 'Copiar ID'}>
											<IconButton size="small" onClick={handleCopyLineId}>
												{copiedLineId ? (
													<CheckIcon fontSize="small" sx={{ color: 'green' }} />
												) : (
													<ContentCopyIcon fontSize="small" />
												)}
											</IconButton>
										</Tooltip>
									</Box>
								</>
							)}
						</Box>
						<IconButton onClick={onClose} size="small">
							<CloseIcon />
						</IconButton>
					</Box>
				</DialogTitle>

				<DialogContent>
					{line && (
						<Box display="flex" gap={3} sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1.5, mb: 2 }}>
							<Box>
								<Typography variant="caption" color="text.secondary">
									A pagar
								</Typography>
								<Typography variant="subtitle2" fontWeight="bold">
									{line.currency} {line.payableAmount.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
								</Typography>
							</Box>

							<Divider orientation="vertical" flexItem />

							<Box>
								<Typography variant="caption" color="text.secondary">
									Pagado
								</Typography>
								<Typography
									variant="subtitle2"
									fontWeight="bold"
									color={totalPaid > 0 ? 'success.main' : 'text.primary'}
								>
									{line.currency} {totalPaid.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
								</Typography>
							</Box>

							<Divider orientation="vertical" flexItem />

							<Box>
								<Typography variant="caption" color="text.secondary">
									Pendiente
								</Typography>
								<Typography
									variant="subtitle2"
									fontWeight="bold"
									color={remaining > 0 ? 'warning.main' : remaining < 0 ? 'error.main' : 'success.main'}
								>
									{line.currency} {remaining.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
								</Typography>
							</Box>
						</Box>
					)}

					{error && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{error}
						</Alert>
					)}

					{loading ? (
						<Box display="flex" justifyContent="center" py={3}>
							<CircularProgress size={28} />
						</Box>
					) : (
						<>
							{payments.length > 0 ? (
								<Table size="small" sx={{ mb: 2 }}>
									<TableHead>
										<TableRow>
											<TableCell>Fecha</TableCell>
											<TableCell>Tipo</TableCell>
											<TableCell align="right">Monto</TableCell>
											<TableCell>Nota</TableCell>
											<TableCell>Observación</TableCell>
											<TableCell>Registrado por</TableCell>
											<TableCell />
										</TableRow>
									</TableHead>
									<TableBody>
										{payments.map((payment) => (
											<TableRow key={payment.id} hover>
												<TableCell>{payment.paymentDate}</TableCell>
												<TableCell>
													<Chip
														label={PAYMENT_TYPE_LABELS[payment.paymentType] ?? payment.paymentType}
														color={PAYMENT_TYPE_COLORS[payment.paymentType] ?? 'default'}
														size="small"
													/>
												</TableCell>
												<TableCell
													align="right"
													sx={{ fontWeight: 'bold', color: payment.amount < 0 ? 'error.main' : 'inherit' }}
												>
													{payment.amount < 0 ? '−' : ''}
													{Math.abs(payment.amount).toLocaleString('es-UY', { minimumFractionDigits: 2 })}
												</TableCell>
												<TableCell>
													<Tooltip title={payment.notes ?? ''}>
														<Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
															{payment.notes ?? '—'}
														</Typography>
													</Tooltip>
												</TableCell>
												<TableCell>
													<Tooltip title={payment.observation ?? ''}>
														<Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
															{payment.observation ?? '—'}
														</Typography>
													</Tooltip>
												</TableCell>
												<TableCell>
													<Typography variant="caption" color="text.secondary">
														{payment.createdBy}
														<br />
														{formatDateTimeToShowUser(payment.created)}
													</Typography>
												</TableCell>
												<TableCell>
													{canEdit && (
														<IconButton size="small" onClick={() => handleEdit(payment)}>
															<EditIcon fontSize="small" />
														</IconButton>
													)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							) : (
								!showForm && (
									<Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
										No hay pagos registrados para esta línea.
									</Typography>
								)
							)}

							{canEdit &&
								(showForm ? (
									<Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mt: 1 }}>
										<PaymentForm
											existingPayment={editingPayment ?? undefined}
											onSubmit={handleSave}
											onCancel={handleCancelForm}
											loading={saving}
											// Acá está el ajuste clave:
											// el maxAmount se manda redondeado a 2 decimales para evitar
											// falsos errores del tipo "0.66 supera 0.66"
											maxAmount={maxAmountForForm}
											currency={line?.currency ?? ''}
										/>
									</Box>
								) : (
									<Box display="flex" justifyContent="flex-end" mt={1}>
										<Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddNew}>
											Agregar pago
										</Button>
									</Box>
								))}
						</>
					)}
				</DialogContent>
			</Dialog>

			<Dialog open={confirmOpen} onClose={handleCancelConfirm} maxWidth="xs" fullWidth>
				<DialogTitle>¿Confirmar cambios?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Estás por modificar un pago registrado. ¿Estás seguro que querés guardar los cambios?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelConfirm} variant="outlined">
						Cancelar
					</Button>
					<Button onClick={handleConfirmEdit} variant="contained" disabled={saving}>
						Sí, guardar cambios
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
